import { useEffect, useRef, useState } from 'react'

import {
  createInitialSource,
  createStageImage,
  decodeImage,
  getStageImageSize,
  getTrimmedStageImage,
  hasIntrinsicSize,
  isObjectUrl,
} from './useStageImageSource.utils'

// Owns the crop source (picked File / existing URL) and its decoded <img>:
// object-URL bookkeeping, load + decode, loading/error flags.
export function useStageImageSource({ initialFile, initialImageUrl }) {
  const objectUrlsRef = useRef(new Set())

  const [source, setSource] = useState(() =>
    createInitialSource({ initialFile, initialImageUrl })
  )
  const [image, setImage] = useState(null)
  const [naturalSize, setNaturalSize] = useState(null)
  const [isImageLoading, setImageLoading] = useState(Boolean(source?.url))
  const [isLoadError, setLoadError] = useState(false)

  const openSourceRef = useRef(source)
  const appliedUrlRef = useRef(source?.url ?? null)

  // Adopt a URL that resolves after mount: the edit flow opens the modal in its
  // skeleton and fetches the signed source while it is already shown. File
  // sources are owned by handleSelectFile, so only URL changes sync here.
  useEffect(() => {
    if (initialImageUrl && initialImageUrl !== appliedUrlRef.current) {
      const nextSource = { name: 'image', url: initialImageUrl }

      appliedUrlRef.current = initialImageUrl
      openSourceRef.current = nextSource
      setImageLoading(true)
      setLoadError(false)
      setSource(nextSource)
    }
  }, [initialImageUrl])

  // Created object URLs are collected here (in an effect, keeping ref writes
  // out of render) and revoked together on unmount.
  useEffect(() => {
    if (isObjectUrl(source?.url)) {
      objectUrlsRef.current.add(source.url)
    }
  }, [source])

  // Load + decode. Keyed on source ONLY, so a stage resize never re-decodes.
  useEffect(() => {
    let isCancelled = false

    if (source?.url) {
      const nextImage = createStageImage()

      function reveal() {
        if (!isCancelled) {
          if (hasIntrinsicSize(nextImage)) {
            const stageImage = getTrimmedStageImage(nextImage)

            setImage(stageImage)
            setNaturalSize(getStageImageSize(stageImage))
          } else {
            setImageLoading(false)
            setLoadError(true)
          }
        }
      }

      // Decode before revealing so the stage mounts an already-decoded element
      // (no flash); reveal on rejection too so a decode failure doesn't strand
      // the loader.
      nextImage.onload = () => decodeImage(nextImage).then(reveal, reveal)

      nextImage.onerror = () => {
        if (!isCancelled) {
          setImageLoading(false)
          setLoadError(true)
        }
      }

      nextImage.src = source.url
    }

    return () => {
      isCancelled = true
    }
  }, [source])

  useEffect(() => {
    const objectUrls = objectUrlsRef.current

    return () => {
      objectUrls.forEach(url => URL.revokeObjectURL(url))
      objectUrls.clear()
    }
  }, [])

  function handleSelectFile(file) {
    setImageLoading(true)
    setLoadError(false)
    setSource({ name: file.name, url: URL.createObjectURL(file) })
  }

  // Reloads the source the pop-up opened with (returns false when it is still
  // current). Compared by url, not reference — the create flow shares one
  // source object, so a reference check would no-op.
  function handleRestoreOpenSource() {
    const openSource = openSourceRef.current

    if (source?.url !== openSource?.url) {
      setImageLoading(true)
      setLoadError(false)
      setSource(openSource)

      return true
    }

    return false
  }

  return {
    fileName: source?.name ?? null,
    handleRestoreOpenSource,
    handleSelectFile,
    image,
    isImageLoading,
    isLoadError,
    naturalSize,
    setImageLoading,
    source,
  }
}
